import { Component, OnDestroy, inject } from '@angular/core';
import { TasksClientProvider, TaskImportDto, TasksImportListDto } from "src/app/web-api/web-api";
import { HelpDialogService } from 'src/app/common-components/dialogs';
import { TimeSpan } from "src/app/services/cst-date-time.service";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { CstListViewDataSource, CstListViewRowStyle, ICstListViewColumn } from "src/app/common-components/list-view/models";
import { EMPTY, from, Observable, of, Subject } from "rxjs";
import { bufferCount, catchError, concatMap, finalize, map, takeUntil, tap, toArray } from "rxjs/operators";
import { AlertService } from "src/app/services";

const CHUNK_SIZE = 100;

class TaskImportViewModel extends TaskImportDto {
    public get DateRangesActiveDisplay() {
        return this.dateRangesActive ? "Ja" : "Nein";
    }

    public get PeriodOfActionDisplay(): string {
        return this.dateRanges && this.dateRanges.periodOfAction
            ? this.dateRanges.periodOfAction.toString()
            : null;
    }
    public get PlannedWeekDisplay(): string {
        return this.dateRanges && this.dateRanges.plannedWeek
            ? this.dateRanges.plannedWeek.toString()
            : null;
    }
    public get TodoDateDisplay(): string {
        return this.dateRanges && this.dateRanges.todoDate
            ? this.dateRanges.todoDate.toLocaleDateString()
            : null;
    }

    public get StreetDisplay(): string {
        return this.workLocation ? this.workLocation.street : null;
    }
    public get StreetNoDisplay(): string {
        return this.workLocation ? this.workLocation.streetNo : null;
    }
    public get CityDisplay(): string {
        return this.workLocation ? this.workLocation.city : null;
    }
    public get PostalCodeDisplay(): string {
        return this.workLocation ? this.workLocation.postalCode : null;
    }
    public get DriveTimePlannedDisplay(): string {
        return this.workLocation && this.workLocation.driveTimePlanned
            ? TimeSpan.fromString(this.workLocation.driveTimePlanned).toStringShort()
            : null;
    }
    public get KilometresPlannedDisplay(): string {
        return this.workLocation
            ? this.workLocation.kilometresPlanned.toString()
            : null;
    }

    public get WorkTimePlannedDisplay(): string {
        return this.workTimePlanned
            ? TimeSpan.fromString(this.workTimePlanned).toStringShort()
            : null;
    }

    public get FormulareDisplay(): string {
        if (!this.formularNamesRaw || this.formularNamesRaw.length === 0) {
            return "";
        }
        return this.formularNamesRaw.filter(x => x).join(", ");
    }

    constructor(dto: TaskImportDto) {
        super();
        Object.assign(this, dto);
    }
}

@Component({
    selector: "app-task-import-csv",
    templateUrl: "./task-import-csv.component.html",
    styleUrls: ["./task-import-csv.component.scss"],
    standalone: false
})
export class PlanungApiTaskImportCsvComponent extends CstResponsivePopupComponentBase implements OnDestroy {
    private _client = inject(TasksClientProvider);
    private _hlp = inject(HelpDialogService);
    private _alert = inject(AlertService);

    public csvFile: File;
    public importGridData: TaskImportViewModel[];
    public hasAnyErrors = false;
    public importIsLoading = false;
    public progress = 0;
    private _destroy$ = new Subject<void>();

    columns: ICstListViewColumn<TaskImportViewModel>[] = [
        { header: $localize`:@@task:Task`, field: "label", width: "200px" },
        { header: $localize`:@@Description:Description`, field: "description", width: "500px" },
        { header: $localize`:@@MeantFor:Assigned To`, field: "assignedTo", width: "200px" },
        { header: $localize`:@@timeperiodactive:Time period active`, field: "DateRangesActiveDisplay", width: "200px" },
        { header: $localize`:@@todoin:To do in`, field: "PeriodOfActionDisplay", width: "200px" },
        { header: $localize`:@@plannedweek:Planned for CW`, field: "PlannedWeekDisplay", width: "200px" },
        { header: $localize`:@@planneddate:Planned date`, field: "TodoDateDisplay", width: "200px" },
        { header: $localize`:@@List_Header_AnlageNr:Facility-No.`, field: "anlageNrIntern", width: "200px" },
        { header: $localize`:@@FacilityGroup:Facility group`, field: "anlageGroup", width: "200px" },
        { header: $localize`:@@street:Street`, field: "StreetDisplay", width: "200px" },
        { header: $localize`:@@HouseNr:Street No.`, field: "StreetNoDisplay", width: "200px" },
        { header: $localize`:@@location:Location`, field: "CityDisplay", width: "200px" },
        { header: $localize`:@@TaskImportCsv_Header_PostalCode:Postal Code`, field: "PostalCodeDisplay", width: "200px" },
        { header: $localize`:@@drivingtime:Planned driving time`, field: "DriveTimePlannedDisplay", width: "200px" },
        { header: $localize`:@@plannedkm:Planned kilometer`, field: "KilometresPlannedDisplay", width: "200px" },
        { header: $localize`:@@targetworktime:Target worktime`, field: "WorkTimePlannedDisplay", width: "200px" },
        { header: $localize`:@@form:Form`, field: "FormulareDisplay", width: "200px" },
    ]

    dataSource: CstListViewDataSource<TaskImportViewModel> = new CstListViewDataSource<TaskImportViewModel>();

    public constructor() {
        super();
        this.dataSource.registerFetchDataCallback(this._onFetchData.bind(this));
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public ItemHasErrors(dataItem: TaskImportViewModel) {
        return dataItem.hasError;
    }

    public OnUpload(ev: File) {
        this.csvFile = ev;
        this.dataSource.refresh();
    }

    public OnStartImport() {
        if (this.importIsLoading || !this.importGridData?.length) return;
        this.importIsLoading = true;
        this.progress = 0;

        const totalChunks = Math.ceil(this.importGridData.length / CHUNK_SIZE);

        from(this.importGridData).pipe(
            bufferCount(CHUNK_SIZE),
            concatMap((chunk, index) => {
                return this._client.importTasks(new TasksImportListDto({ tasks: chunk })).pipe(
                    tap(() => this.progress = Math.round(((index + 1) / totalChunks) * 100))
                );
            }),
            toArray(),
            finalize(() => {
                this.importIsLoading = false;
                this.progress = 0;
            }),
            catchError(error => {
                this._alert.showSnackbar("Import: failed (see console)", { duration: "short", "type": "error" });
                console.error(error);
                return EMPTY;
            }),
            takeUntil(this._destroy$)
        ).subscribe({
            next: () => {
                this._alert.showSnackbar("Import: success", { duration: "short", "type": "info" });
                this.close(true);
            }
        });
    }

    public OnOpenCsvSpecification() {
        this._client.importTasksCsvSpecification().subscribe(result => {
            this._hlp.DisplayHtml(result.data, "Hilfe zur Formatierung der CSV-Datei");
        });
    }

    public RowCallback(element: TaskImportViewModel): CstListViewRowStyle {
        return element.hasError ? "error" : "default";
    }

    private _onFetchData(): Observable<{ data: TaskImportViewModel[]; total: number }> {
        const file = {
            fileName: this.csvFile.name,
            data: this.csvFile
        };
        console.log(file);

        this.hasAnyErrors = false;

        return this._client.parseCSV(file).pipe(
            map(result => result.tasks.map(t => new TaskImportViewModel(t))),
            tap(data => {
                this.importGridData = data;
                this.hasAnyErrors = this.hasAnyErrors || data.some(t => t.hasError);
            }),
            map(result => ({ data: result, total: result.length })),
            catchError(error => {
                this._alert.showSnackbar("Import: error (see console)", { duration: "short", "type": "error" })
                console.error(error);
                return of({ data: [], total: 0 });
            }),
            takeUntil(this._destroy$)
        );
    }
}
